import { Alert, Button, Stack, TextField } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader.tsx";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import type { ReportGroup } from "../../../../../../api/types/svTypes.ts";
import validateForm from "../utilities/validateForm.ts";
import type { FromReport } from "./FromReport.ts";
import ReportGroupSelect from "./ReportGroupSelect.tsx";

export interface Props {
  report: FromReport;
  groups?: ReportGroup[];
  title?: string;
  saveBtnTitle?: string;
  showName?: boolean;
  showLevel?: boolean;
  error?: string;
  saving: boolean;
  disabled?: boolean;
  onClose: () => void;
  onSave: (name: string, groupId: string) => void;
}

export default function SaveReportForm({
  report,
  groups,
  title,
  saveBtnTitle,
  error,
  saving,
  showLevel,
  showName,
  onClose,
  onSave,
  disabled,
}: Props) {
  const { hasAnyPermissionForClient } = useClientPermissionsContext();

  const isReportAdmin = hasAnyPermissionForClient(report.clientCode, ["ManageAllReports"]);
  const canManageReports = hasAnyPermissionForClient(report.clientCode, ["ManageReports"]);
  const canCreateGroup = isReportAdmin || (canManageReports && groups?.length === 0);

  const [name, setName] = useState(report.name);

  const [groupId, setGroupId] = useState(() => {
    const group = groups?.find((g) => g.id === report.groupId);
    return group?.id || (groups && groups[0]?.id) || "";
  });
  const isNameValid = name.trim().length >= 2;

  const isFormValid = validateForm(name.trim(), groupId, showName, showLevel);
  const handleSave = () => onSave(name.trim(), groupId);

  return (
    <>
      <DialogHeader text={title || ""} onClose={onClose} />
      {error && (
        <Alert sx={{ mb: 2, mx: 2.5 }} severity="error">
          {error}
        </Alert>
      )}
      <Stack spacing={2} useFlexGap sx={{ px: 2.5, py: 1, flex: 1, justifyContent: "center", width: "100%" }}>
        {showName && (
          <TextField
            fullWidth
            value={name}
            onChange={(evt) => setName(evt.target.value)}
            error={!isNameValid}
            helperText={!isNameValid ? "Name should be at least 2 characters long." : ""}
            label="Name"
            disabled={saving || disabled}
          />
        )}
        {showLevel && (
          <ReportGroupSelect
            clientCode={report.clientCode}
            value={groupId}
            groups={groups}
            disabled={saving || disabled}
            canCreateGroup={canCreateGroup}
            onChange={setGroupId}
          />
        )}
      </Stack>
      <DialogButtons cancelDisabled={saving} onCancel={onClose} cancelText="Close">
        <Button variant="contained" disabled={saving || !isFormValid || disabled} loading={saving} onClick={handleSave}>
          {saveBtnTitle}
        </Button>
      </DialogButtons>
    </>
  );
}
